import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {GeoAnalyticsEvent, GeoLocationAnalytics} from "../geo-location-analytics";
import {CGPEventSubscription} from "../../sdk/cgp-sdk.interface";
import {
    GeoValidationResult,
    IGeoComplyFailure,
    IGeoComplyResponse
} from "../strategies/geo-location.strategy.interface";
import {
    GENERIC_GEO_ERROR_CODE,
    GeoComplyFailureReason,
    isGeoComplyPluginInstallationRequired
} from "../strategies/geo-comply-error-codes";
import {TimeSpan} from "@sparkware/app-utils";
import {UserController_ValidateGeoLocationRequest} from "../../api/api.service";
import {IGeoRestrictionsUpdateEventParams} from "../../sdk/sdk-events";
import {isValidGeoKickEvent} from "./geo-kick-reasons";
import {formatErrorForLogging} from "../geo-helpers";

export interface IPlatformGeoValidationResult {
    result: GeoValidationResult;
    kickParams?: IGeoRestrictionsUpdateEventParams;
}

export class PlatformGeoLocationValidator {
    constructor(private readonly services: IServiceFactoryExtended, private readonly _analytics: GeoLocationAnalytics) {
        this._waitForRestrictionsPromise = this._waitForPlatformToResolveGeoRestrictions();
    }

    private _updateRestrictionTimeoutRef: any = null;
    private _sdkEventsSubscriptions: CGPEventSubscription[] = [];

    private _clearUpdateRestrictionsTimeoutRef(): void {
        if(this._updateRestrictionTimeoutRef) {
            clearTimeout(this._updateRestrictionTimeoutRef);
            this._updateRestrictionTimeoutRef = null;
        }
    }

    private _unsubscribeFromSdkEvents(){
        this._sdkEventsSubscriptions.forEach(event => {
            event.off();

        });

        this._sdkEventsSubscriptions = [];
    }

    private _cancelAllSubscriptions(): void {
        this._clearUpdateRestrictionsTimeoutRef();
        this._unsubscribeFromSdkEvents();
    }

    private readonly _waitForRestrictionsPromise: Promise<IPlatformGeoValidationResult>
    private _waitForPlatformToResolveGeoRestrictions(): Promise<IPlatformGeoValidationResult> {

        return new Promise<IPlatformGeoValidationResult>(resolve => {

            const resolvePromise = (kickInfo?: IGeoRestrictionsUpdateEventParams) => {
                if(isValidGeoKickEvent(kickInfo)) {
                    resolve({
                        result: GeoValidationResult.Fail,
                        kickParams: kickInfo
                    });
                } else {
                    this.services.geoLocation.isUserBlockedByGeo().then(isBlocked => {
                        if(isBlocked) {
                            resolve({
                                result: GeoValidationResult.Fail
                            });
                        } else {
                            resolve({
                                result: GeoValidationResult.Pass
                            });
                        }
                    });
                }

            }

            //After platform process the geo result it will send to the client a restrictions update event either the geo pass or failed.
            //And basically this is the indication for us that the geo flow is completed.
            this._sdkEventsSubscriptions.push(this.services.sdk.events.onGeoRestrictionsUpdate((kickInfo) => {
                this._analytics.reportEvent({
                    geoEventName: GeoAnalyticsEvent.UpdateRestrictionsReceived,
                    kickParams: kickInfo
                });

                this._cancelAllSubscriptions();
                resolvePromise(kickInfo);
            }));


            //However if the restrictions change event never come because firebase issues or whatever reason
            //we don't want to hang forever, so we have 1 minute timeout here.
            this._updateRestrictionTimeoutRef = setTimeout(() => {
                this._unsubscribeFromSdkEvents();
                this._analytics.reportEvent({
                    geoEventName: GeoAnalyticsEvent.UpdateRestrictionsTimedOut,
                    failureReason: GeoComplyFailureReason.TechnicalProblem
                });
                resolvePromise();

            }, TimeSpan.fromMinutes(1).totalMilliseconds);
        })
    }


    private async _executeCgpValidation(request: UserController_ValidateGeoLocationRequest): Promise<GeoValidationResult> {
        try {
            await this.services.api.userController.validateGeoLocation(request);
            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.CGPValidationSuccess
            });
            return GeoValidationResult.Pass;
        } catch (err) {
            this._analytics.reportEvent({
                geoEventName: GeoAnalyticsEvent.CGPValidationFailure,
                errorCode: GENERIC_GEO_ERROR_CODE,
                errorMessage: formatErrorForLogging(err),
                failureReason: GeoComplyFailureReason.TechnicalProblem
            });
            return GeoValidationResult.Fail;
        }

    }

    private async _validateGeoSuccessResponse(options: {data: string; isPeriodicFlow: boolean}): Promise<IPlatformGeoValidationResult> {
        const {data, isPeriodicFlow} = options;
        const cgpValidationResult = await this._executeCgpValidation({
            geoLocationEncryptedResponse: data,
            isPeriodicFlow: isPeriodicFlow
        });

        if(cgpValidationResult !== GeoValidationResult.Pass) {
            return {
                result: cgpValidationResult
            };
        }

        return await this._waitForRestrictionsPromise;
    }

    private async _validateGeoFailureResponse(options: {failure: IGeoComplyFailure; isPeriodicFlow: boolean}): Promise<IPlatformGeoValidationResult> {
        const {failure, isPeriodicFlow} = options;
        if(isGeoComplyPluginInstallationRequired(failure.geoComplyError?.errorCode)) {
            return {
                result: GeoValidationResult.Fail
            };
        }

        const validateGeoRequest: UserController_ValidateGeoLocationRequest = {
            geoLocationEncryptedResponse: '',
            errorCode: failure.geoComplyError?.errorCode?.toString() ?? "",
            errorMsg: failure.geoComplyError?.errorMessage ?? "",
            isPeriodicFlow: isPeriodicFlow
        }

        if(failure.additionalFailureReason) {
            validateGeoRequest.failedLocationReason = failure.additionalFailureReason;
        } else {
            validateGeoRequest.failedLocationReason = GeoComplyFailureReason.TechnicalProblem
        }

        const cgpValidationResult = await this._executeCgpValidation(validateGeoRequest);

        if(cgpValidationResult !== GeoValidationResult.Pass) {
            return {
                result: cgpValidationResult
            };
        }

        return await this._waitForRestrictionsPromise;
    }

    public async validateGeoResult(options: {geoComplyResponse: IGeoComplyResponse; isPeriodicFlow: boolean}): Promise<IPlatformGeoValidationResult> {
        const {geoComplyResponse, isPeriodicFlow} = options;
        try {
            if(geoComplyResponse.data) {

                return await this._validateGeoSuccessResponse({data: geoComplyResponse.data, isPeriodicFlow: isPeriodicFlow});

            } else if(geoComplyResponse.failure) {

                return await this._validateGeoFailureResponse({failure: geoComplyResponse.failure, isPeriodicFlow: isPeriodicFlow});

            } else {
                this._analytics.reportEvent({
                    geoEventName: GeoAnalyticsEvent.GeoComplyInvalidResponse,
                    errorCode: GENERIC_GEO_ERROR_CODE,
                    errorMessage: `GeoComply didn't return any data or error`,
                    failureReason: GeoComplyFailureReason.TechnicalProblem
                })
                return {
                    result: GeoValidationResult.Fail
                };
            }
        } finally {
            this._cancelAllSubscriptions();
        }

    }
}